import React, { useState } from 'react';
import { useParams } from 'react-router';
import { useGetDistributionQuery } from 'features/getDistributions/useGetDistributions';
import { StyledCard } from 'shared/ui/Card/StyledCard';
import { MoreInfo } from 'shared/ui/MoreInfo/MoreInfo';
import { Box, CircularProgress, Stack, Tab } from '@mui/material';
import { getDate } from 'shared/utils/date';
import { isEmptyArr } from 'shared/utils/isEmpty';
import { useGetCitiesQuery } from 'features/getCities/api/getCitiesApi';
import { useGetB2BSalesPointsQuery } from 'features/getB2BConstants/b2bConstants';
import { useGetSourcesQuery } from 'features/getSources/api/getSourcesApi';
import { TabContext, TabList } from '@mui/lab';

const notificationTypes = [
  { title: 'Акции', value: 'action' },
  { title: 'Персональное', value: 'personal' },
  { title: 'Системное', value: 'system' },
];

const langs = [
  { value: '1', label: 'RU' },
  { value: '2', label: 'KZ' },
  { value: '3', label: 'EN' },
  { value: '4', label: 'KG' },
  { value: '5', label: 'UZ' },
];

export const DistributionDetail = () => {
  const [tabValue, setTabValue] = useState('1');
  const { id } = useParams();
  const distribution = useGetDistributionQuery(id);
  const cities = useGetCitiesQuery({});
  const constants = useGetB2BSalesPointsQuery('constants');
  const sources = useGetSourcesQuery('sources');

  if (distribution.isFetching || cities.isFetching || constants.isFetching || sources.isFetching) {
    return (
      <Stack alignItems="center">
        <CircularProgress />
      </Stack>
    );
  }

  if (distribution.isError || cities.isError || constants.isError || sources.isError) {
    return <Stack alignItems="center">ERROR</Stack>;
  }

  function a11yProps(index: number) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  }

  const handleChangeTabValue = (event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
  };

  const getLang = () =>
    // @ts-ignore
    langs.find((item) => item.value === tabValue).label.toLowerCase();
  return distribution.isSuccess && cities.isSuccess && constants.isSuccess && sources.isSuccess ? (
    <Box
      component={StyledCard}
      sx={{ marginBottom: '24px' }}
    >
      <Box style={{ display: 'flex', marginTop: '12px' }}>
        <div
          className="column"
          style={{ width: '250px' }}
        >
          <MoreInfo>
            <div className="title">ID</div>
            <div className="content">{distribution.data.id}</div>
          </MoreInfo>
          <MoreInfo>
            <div className="title">Направления</div>

            <div className="content">
              {!isEmptyArr(distribution.data.app_types as any[]) ? (
                <>{distribution.data.app_types.join(', ').toUpperCase()}</>
              ) : (
                '---'
              )}
            </div>
          </MoreInfo>
        </div>
        <div
          className="column"
          style={{ width: '250px' }}
        >
          <MoreInfo>
            <div className="title">Дата создания</div>
            <div className="content">{getDate(distribution.data.created_dttm, true)}</div>
          </MoreInfo>
          <MoreInfo>
            <div className="title">Города</div>
            <div className="content">
              {!isEmptyArr(distribution.data.city_ids as any[]) ? (
                <>
                  {cities.data
                    .filter((item) => distribution.data.city_ids.includes(item.id))
                    .map((item) => (
                      <div key={item.id}>{item.name} </div>
                    ))}
                </>
              ) : (
                '---'
              )}
            </div>
          </MoreInfo>
        </div>

        {distribution.data.action_button && (
          <div
            className="column"
            style={{ width: '250px' }}
          >
            <MoreInfo>
              <div className="title">Название кнопки</div>
              <div className="content">{distribution.data.action_button.label || '---'}</div>
            </MoreInfo>
            <MoreInfo>
              <div className="title">URL кнопки</div>
              <div className="content">
                {distribution.data.action_button.url ? (
                  <a
                    target="_blank"
                    rel="noreferrer"
                    href={distribution.data.action_button.url}
                  >
                    {distribution.data.action_button.url}
                  </a>
                ) : (
                  '---'
                )}
              </div>
            </MoreInfo>
          </div>
        )}
      </Box>

      <Box style={{ display: 'flex', marginTop: '12px' }}>
        <div
          className="column"
          style={{ width: '250px' }}
        >
          <MoreInfo>
            <div className="title">Каналы B2B</div>
            <div className="content">
              {!isEmptyArr(distribution.data.b2b_directions as any[]) ? (
                <>
                  {distribution.data.b2b_directions
                    .map((item: any) =>
                      Object.keys(constants.data['SalesPoint.Direction']).find(
                        (key) => constants.data['SalesPoint.Direction'][key] === item,
                      ),
                    )
                    .join(', ')}
                </>
              ) : (
                '---'
              )}
            </div>
          </MoreInfo>
          <MoreInfo>
            <div className="title">Уровни торговых точек</div>
            <div className="content">
              {!isEmptyArr(distribution.data.b2b_directions as any[]) ? (
                <>
                  {distribution.data.b2b_directions
                    .map((item: any) =>
                      Object.keys(constants.data['SalesPoint.Tier']).find(
                        (key) => constants.data['SalesPoint.Tier'][key] === item,
                      ),
                    )
                    .join(', ')}
                </>
              ) : (
                '---'
              )}
            </div>
          </MoreInfo>
        </div>
        <div
          className="column"
          style={{ width: '250px' }}
        >
          <MoreInfo>
            <div className="title">Тип уведомления</div>
            <div className="content">
              {notificationTypes
                .filter((item) => item.value === distribution.data.notification_type)
                .map((item) => (
                  <div key={item.value}>{item.title}</div>
                ))}
            </div>
          </MoreInfo>
          <MoreInfo>
            <div className="title">Источники</div>
            <div className="content">
              {!isEmptyArr(distribution.data.source_ids as any[]) ? (
                <>
                  {sources.data
                    .filter((item: { id: number; name: string }) => distribution.data.source_ids.includes(item.id))
                    .map((item: { id: number; name: string }) => (
                      <div key={item.id}>{item.name} </div>
                    ))}
                </>
              ) : (
                '---'
              )}
            </div>
          </MoreInfo>
        </div>
        <div
          className="column"
          style={{ width: '250px' }}
        >
          <MoreInfo>
            <div className="title">Тип ссылки</div>
            <div className="content">
              {distribution.data.action_button && distribution.data.action_button.action_type === 'external_link'
                ? 'Внешняя ссылка'
                : distribution.data.action_button && distribution.data.action_button.action_type === 'deep_link'
                ? 'Диплинк'
                : '---'}
            </div>
          </MoreInfo>
        </div>
      </Box>

      <TabContext value={tabValue.toString()}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <TabList
            onChange={handleChangeTabValue}
            aria-label="basic tabs example"
          >
            {langs.map((lang) => (
              <Tab
                key={lang.value}
                value={lang.value}
                label={lang.label}
                {...a11yProps(+lang.value)}
              />
            ))}
          </TabList>
        </Box>
      </TabContext>

      <div
        className="column"
        style={{ width: '400px', marginTop: 24 }}
      >
        <MoreInfo>
          <div className="title">Название</div>
          <div className="content">{distribution.data[`title_${getLang()}`] || '---'}</div>
        </MoreInfo>
        <MoreInfo>
          <div className="title">Описание</div>
          <div className="content">{distribution.data[`content_${getLang()}`] || '---'}</div>
        </MoreInfo>
        <MoreInfo>
          <div className="title">Фото</div>
          <div className="content">
            {distribution.data.image_url ? (
              <Box
                component="img"
                src={distribution.data.image_url}
                sx={{ height: '140px', width: '140px', objectFit: 'cover' }}
              />
            ) : (
              '---'
            )}
          </div>
        </MoreInfo>
      </div>
    </Box>
  ) : null;
};
